// Translates recipe quantities into commercially available pack sizes
// for Austrian/Central European retail stores.

export interface PackSize {
  amount: number
  unit: 'g' | 'ml' | 'stk'
  label: string
  notes?: string
}

export interface ResolvedPack {
  ingredient: string
  weekly_total_g_or_ml: number
  base_unit: 'g' | 'ml' | 'stk'
  recommended_pack: PackSize
  packs_needed: number
  shopping_label: string
}

// Ingredients that stores sell by count (stk) but recipes specify by weight.
// Converts grams → unit count before pack size lookup.
const GRAM_TO_UNIT: Record<string, number> = {
  'eggs':     60,   // 1 medium egg ≈ 60g
  'banana':   120,  // 1 medium banana ≈ 120g
  'avocado':  175,
  'lemon':    115,
  'garlic':   60,   // 1 garlic bulb ≈ 60g
  'apple':    170,
  'cucumber': 350,
}

// Ingredients with a different natural retail unit — convert before pack lookup.
const UNIT_CONVERSIONS: Record<string, { multiply: number; target_unit: 'g' | 'ml' | 'stk' }> = {
  'olive oil':     { multiply: 1 / 0.92, target_unit: 'ml' },
  'coconut oil':   { multiply: 1 / 0.92, target_unit: 'ml' },
  'rapeseed oil':  { multiply: 1 / 0.92, target_unit: 'ml' },
  'sunflower oil': { multiply: 1 / 0.92, target_unit: 'ml' },
  'sesame oil':    { multiply: 1 / 0.92, target_unit: 'ml' },
  'milk':          { multiply: 1,         target_unit: 'ml' },
  'oat milk':      { multiply: 1,         target_unit: 'ml' },
  'cream':         { multiply: 1,         target_unit: 'ml' },
}

const PACK_SIZES: Record<string, PackSize[]> = {
  // ── MEAT ──
  'chicken breast': [
    { amount: 400,  unit: 'g', label: '400g', notes: 'most common at Hofer/Lidl' },
    { amount: 500,  unit: 'g', label: '500g' },
    { amount: 600,  unit: 'g', label: '600g' },
    { amount: 800,  unit: 'g', label: '800g' },
    { amount: 1000, unit: 'g', label: '1kg' },
  ],
  'beef': [
    { amount: 400,  unit: 'g', label: '400g' },
    { amount: 500,  unit: 'g', label: '500g' },
    { amount: 750,  unit: 'g', label: '750g' },
    { amount: 1000, unit: 'g', label: '1kg' },
  ],
  'minced beef': [
    { amount: 400, unit: 'g', label: '400g' },
    { amount: 500, unit: 'g', label: '500g' },
    { amount: 750, unit: 'g', label: '750g' },
  ],
  'ground beef': [
    { amount: 400, unit: 'g', label: '400g' },
    { amount: 500, unit: 'g', label: '500g' },
    { amount: 750, unit: 'g', label: '750g' },
  ],
  'pork': [
    { amount: 400,  unit: 'g', label: '400g' },
    { amount: 500,  unit: 'g', label: '500g' },
    { amount: 1000, unit: 'g', label: '1kg' },
  ],
  'pork tenderloin': [
    { amount: 400, unit: 'g', label: '400g' },
    { amount: 500, unit: 'g', label: '500g' },
  ],
  'pork loin': [
    { amount: 400,  unit: 'g', label: '400g' },
    { amount: 500,  unit: 'g', label: '500g' },
    { amount: 1000, unit: 'g', label: '1kg' },
  ],
  'lamb': [
    { amount: 300, unit: 'g', label: '300g' },
    { amount: 400, unit: 'g', label: '400g' },
    { amount: 500, unit: 'g', label: '500g' },
  ],
  'turkey': [
    { amount: 300, unit: 'g', label: '300g' },
    { amount: 400, unit: 'g', label: '400g' },
    { amount: 500, unit: 'g', label: '500g' },
  ],
  'turkey breast': [
    { amount: 300, unit: 'g', label: '300g' },
    { amount: 400, unit: 'g', label: '400g' },
    { amount: 500, unit: 'g', label: '500g' },
  ],

  // ── FISH ──
  'salmon': [
    { amount: 200, unit: 'g', label: '200g', notes: 'single fresh portion' },
    { amount: 300, unit: 'g', label: '300g' },
    { amount: 400, unit: 'g', label: '400g', notes: 'common frozen pack' },
    { amount: 500, unit: 'g', label: '500g' },
  ],
  'salmon fillet': [
    { amount: 200, unit: 'g', label: '200g' },
    { amount: 300, unit: 'g', label: '300g' },
    { amount: 400, unit: 'g', label: '400g' },
    { amount: 500, unit: 'g', label: '500g' },
  ],
  'tuna': [
    { amount: 150, unit: 'g', label: '150g tin' },
    { amount: 300, unit: 'g', label: '300g tin' },
  ],
  'tilapia': [
    { amount: 300, unit: 'g', label: '300g' },
    { amount: 400, unit: 'g', label: '400g' },
    { amount: 500, unit: 'g', label: '500g' },
  ],

  // ── EGGS ──
  'eggs': [
    { amount: 6,  unit: 'stk', label: '6-pack', notes: 'most common at Hofer/Lidl' },
    { amount: 10, unit: 'stk', label: '10-pack', notes: 'Billa/Spar standard' },
    { amount: 12, unit: 'stk', label: '12-pack' },
    { amount: 20, unit: 'stk', label: '20-pack' },
  ],

  // ── DAIRY ──
  'yogurt': [
    { amount: 200,  unit: 'g', label: '200g cup' },
    { amount: 400,  unit: 'g', label: '400g' },
    { amount: 500,  unit: 'g', label: '500g' },
    { amount: 1000, unit: 'g', label: '1kg' },
  ],
  'greek yogurt': [
    { amount: 150, unit: 'g', label: '150g' },
    { amount: 200, unit: 'g', label: '200g', notes: 'most common single serve' },
    { amount: 400, unit: 'g', label: '400g' },
    { amount: 500, unit: 'g', label: '500g' },
  ],
  'cottage cheese': [
    { amount: 200, unit: 'g', label: '200g tub' },
    { amount: 250, unit: 'g', label: '250g tub', notes: 'most common' },
    { amount: 500, unit: 'g', label: '500g tub' },
  ],
  'milk': [
    { amount: 500,  unit: 'ml', label: '500ml' },
    { amount: 1000, unit: 'ml', label: '1L', notes: 'standard pack' },
    { amount: 1500, unit: 'ml', label: '1.5L' },
  ],
  'oat milk': [
    { amount: 1000, unit: 'ml', label: '1L', notes: 'standard — no smaller available' },
  ],
  'cheese': [
    { amount: 100, unit: 'g', label: '100g' },
    { amount: 150, unit: 'g', label: '150g' },
    { amount: 200, unit: 'g', label: '200g' },
    { amount: 250, unit: 'g', label: '250g' },
    { amount: 400, unit: 'g', label: '400g' },
  ],
  'mozzarella': [
    { amount: 125, unit: 'g', label: '125g ball' },
    { amount: 250, unit: 'g', label: '2× 125g twin pack' },
  ],
  'butter': [
    { amount: 125, unit: 'g', label: '125g block' },
    { amount: 250, unit: 'g', label: '250g block', notes: 'most common' },
  ],
  'cream': [
    { amount: 200, unit: 'ml', label: '200ml' },
    { amount: 250, unit: 'ml', label: '250ml' },
    { amount: 500, unit: 'ml', label: '500ml' },
  ],
  'quark': [
    { amount: 250, unit: 'g', label: '250g' },
    { amount: 500, unit: 'g', label: '500g' },
  ],

  // ── GRAINS ──
  'oats': [
    { amount: 500,  unit: 'g', label: '500g', notes: 'standard at all stores' },
    { amount: 750,  unit: 'g', label: '750g' },
    { amount: 1000, unit: 'g', label: '1kg' },
  ],
  'rice': [
    { amount: 500,  unit: 'g', label: '500g' },
    { amount: 1000, unit: 'g', label: '1kg', notes: 'most common' },
    { amount: 2000, unit: 'g', label: '2kg' },
  ],
  'quinoa': [
    { amount: 200, unit: 'g', label: '200g' },
    { amount: 250, unit: 'g', label: '250g' },
    { amount: 400, unit: 'g', label: '400g' },
    { amount: 500, unit: 'g', label: '500g' },
  ],
  'pasta': [
    { amount: 500, unit: 'g', label: '500g', notes: 'standard at all stores' },
  ],
  'couscous': [
    { amount: 250,  unit: 'g', label: '250g' },
    { amount: 500,  unit: 'g', label: '500g' },
    { amount: 1000, unit: 'g', label: '1kg' },
  ],

  // ── VEGETABLES ──
  'spinach': [
    { amount: 100, unit: 'g', label: '100g bag' },
    { amount: 200, unit: 'g', label: '200g bag' },
    { amount: 300, unit: 'g', label: '300g bag' },
    { amount: 500, unit: 'g', label: '500g bag' },
  ],
  'broccoli': [
    { amount: 400, unit: 'g', label: '1 head (~400g)' },
    { amount: 500, unit: 'g', label: '500g florets bag' },
    { amount: 750, unit: 'g', label: '750g frozen' },
  ],
  'carrots': [
    { amount: 500,  unit: 'g', label: '500g bag' },
    { amount: 750,  unit: 'g', label: '750g bag' },
    { amount: 1000, unit: 'g', label: '1kg bag', notes: 'most economical' },
  ],
  'carrot': [
    { amount: 500,  unit: 'g', label: '500g bag' },
    { amount: 750,  unit: 'g', label: '750g bag' },
    { amount: 1000, unit: 'g', label: '1kg bag' },
  ],
  'sweet potato': [
    { amount: 500,  unit: 'g', label: '500g' },
    { amount: 750,  unit: 'g', label: '750g' },
    { amount: 1000, unit: 'g', label: '1kg' },
  ],
  'potato': [
    { amount: 500,  unit: 'g', label: '500g bag' },
    { amount: 1000, unit: 'g', label: '1kg bag', notes: 'most common' },
    { amount: 1500, unit: 'g', label: '1.5kg bag' },
    { amount: 2000, unit: 'g', label: '2kg bag' },
  ],
  'cucumber': [
    { amount: 350, unit: 'g', label: 'cucumber (~350g)' },
    { amount: 700, unit: 'g', label: '2 cucumbers' },
  ],
  'bell pepper': [
    { amount: 160, unit: 'g', label: 'pepper (~160g)' },
    { amount: 480, unit: 'g', label: '3-pack mixed (~480g)' },
    { amount: 500, unit: 'g', label: '500g bag' },
  ],
  'tomatoes': [
    { amount: 250,  unit: 'g', label: '250g cherry punnet' },
    { amount: 500,  unit: 'g', label: '500g bag' },
    { amount: 1000, unit: 'g', label: '1kg bag' },
  ],
  'tomato': [
    { amount: 250,  unit: 'g', label: '250g punnet' },
    { amount: 500,  unit: 'g', label: '500g bag' },
    { amount: 1000, unit: 'g', label: '1kg bag' },
  ],
  'zucchini': [
    { amount: 250,  unit: 'g', label: 'zucchini (~250g)' },
    { amount: 500,  unit: 'g', label: '2 zucchini' },
    { amount: 1000, unit: 'g', label: '1kg bag' },
  ],
  'onion': [
    { amount: 500,  unit: 'g', label: '500g bag' },
    { amount: 750,  unit: 'g', label: '750g bag' },
    { amount: 1000, unit: 'g', label: '1kg bag' },
    { amount: 2000, unit: 'g', label: '2kg bag' },
  ],
  'garlic': [
    { amount: 1, unit: 'stk', label: 'bulb (~60g)' },
    { amount: 2, unit: 'stk', label: '2-pack bulbs' },
    { amount: 3, unit: 'stk', label: '3-pack bulbs' },
  ],
  'mushrooms': [
    { amount: 200, unit: 'g', label: '200g' },
    { amount: 400, unit: 'g', label: '400g' },
    { amount: 500, unit: 'g', label: '500g', notes: 'most common' },
  ],
  'green beans': [
    { amount: 250, unit: 'g', label: '250g fresh' },
    { amount: 400, unit: 'g', label: '400g frozen' },
    { amount: 500, unit: 'g', label: '500g fresh/frozen' },
    { amount: 750, unit: 'g', label: '750g frozen' },
  ],
  'leek': [
    { amount: 250, unit: 'g', label: 'leek (~250g)' },
    { amount: 500, unit: 'g', label: '2 leeks' },
  ],
  'mixed greens': [
    { amount: 100, unit: 'g', label: '100g bag' },
    { amount: 150, unit: 'g', label: '150g bag' },
    { amount: 200, unit: 'g', label: '200g bag' },
  ],
  'sweet corn': [
    { amount: 340, unit: 'g', label: '340g tin' },
    { amount: 400, unit: 'g', label: '400g frozen bag' },
  ],

  // ── FRUIT ──
  'banana': [
    { amount: 1, unit: 'stk', label: 'banana (~130g)' },
    { amount: 3, unit: 'stk', label: '3 bananas' },
    { amount: 5, unit: 'stk', label: '5 bananas (bunch)' },
  ],
  'apple': [
    { amount: 1,    unit: 'stk', label: 'apple (~170g)' },
    { amount: 1000, unit: 'g',   label: '1kg bag' },
    { amount: 1500, unit: 'g',   label: '1.5kg bag' },
    { amount: 2000, unit: 'g',   label: '2kg bag' },
  ],
  'blueberries': [
    { amount: 125, unit: 'g', label: '125g punnet' },
    { amount: 200, unit: 'g', label: '200g punnet' },
    { amount: 250, unit: 'g', label: '250g punnet' },
    { amount: 400, unit: 'g', label: '400g frozen pack' },
  ],
  'strawberries': [
    { amount: 250, unit: 'g', label: '250g punnet' },
    { amount: 400, unit: 'g', label: '400g' },
    { amount: 500, unit: 'g', label: '500g' },
  ],
  'lemon': [
    { amount: 1, unit: 'stk', label: 'lemon (~115g)' },
    { amount: 3, unit: 'stk', label: '3-pack' },
    { amount: 5, unit: 'stk', label: '5-pack net' },
  ],
  'avocado': [
    { amount: 1, unit: 'stk', label: 'avocado (~175g)' },
    { amount: 2, unit: 'stk', label: '2-pack' },
    { amount: 3, unit: 'stk', label: '3-pack' },
  ],
  'mango': [
    { amount: 1,   unit: 'stk', label: 'mango (~350g)' },
    { amount: 300, unit: 'g',   label: '300g frozen' },
    { amount: 500, unit: 'g',   label: '500g frozen' },
    { amount: 750, unit: 'g',   label: '750g frozen' },
  ],

  // ── LEGUMES ──
  'lentils': [
    { amount: 400,  unit: 'g', label: '400g bag' },
    { amount: 500,  unit: 'g', label: '500g bag', notes: 'most common' },
    { amount: 1000, unit: 'g', label: '1kg bag' },
  ],
  'chickpeas': [
    { amount: 240, unit: 'g', label: '400g tin (240g drained)', notes: 'canned most convenient' },
    { amount: 500, unit: 'g', label: '500g dried bag' },
  ],
  'black beans': [
    { amount: 240, unit: 'g', label: '400g tin (240g drained)' },
    { amount: 500, unit: 'g', label: '500g dried bag' },
  ],
  'kidney beans': [
    { amount: 240, unit: 'g', label: '400g tin (240g drained)' },
    { amount: 400, unit: 'g', label: '400g dried bag' },
  ],
  'edamame': [
    { amount: 300, unit: 'g', label: '300g frozen' },
    { amount: 400, unit: 'g', label: '400g frozen' },
    { amount: 500, unit: 'g', label: '500g frozen' },
  ],

  // ── NUTS & SEEDS ──
  'almonds': [
    { amount: 100, unit: 'g', label: '100g bag' },
    { amount: 150, unit: 'g', label: '150g bag' },
    { amount: 200, unit: 'g', label: '200g bag', notes: 'most common' },
    { amount: 500, unit: 'g', label: '500g bag' },
  ],
  'walnuts': [
    { amount: 100, unit: 'g', label: '100g bag' },
    { amount: 200, unit: 'g', label: '200g bag', notes: 'most common' },
    { amount: 500, unit: 'g', label: '500g bag' },
  ],
  'cashews': [
    { amount: 100, unit: 'g', label: '100g bag' },
    { amount: 150, unit: 'g', label: '150g bag' },
    { amount: 200, unit: 'g', label: '200g bag' },
    { amount: 500, unit: 'g', label: '500g bag' },
  ],
  'mixed nuts': [
    { amount: 100, unit: 'g', label: '100g bag' },
    { amount: 200, unit: 'g', label: '200g bag' },
    { amount: 300, unit: 'g', label: '300g bag' },
    { amount: 500, unit: 'g', label: '500g bag' },
  ],
  'peanut butter': [
    { amount: 350,  unit: 'g', label: '350g jar' },
    { amount: 500,  unit: 'g', label: '500g jar', notes: 'most common' },
    { amount: 1000, unit: 'g', label: '1kg jar' },
  ],
  'chia seeds': [
    { amount: 200, unit: 'g', label: '200g bag' },
    { amount: 250, unit: 'g', label: '250g bag' },
    { amount: 400, unit: 'g', label: '400g bag' },
    { amount: 500, unit: 'g', label: '500g bag', notes: 'most economical' },
  ],
  'flaxseeds': [
    { amount: 250,  unit: 'g', label: '250g bag' },
    { amount: 500,  unit: 'g', label: '500g bag' },
    { amount: 1000, unit: 'g', label: '1kg bag' },
  ],
  'sunflower seeds': [
    { amount: 200, unit: 'g', label: '200g bag' },
    { amount: 250, unit: 'g', label: '250g bag' },
    { amount: 300, unit: 'g', label: '300g bag' },
    { amount: 500, unit: 'g', label: '500g bag' },
  ],
  'pumpkin seeds': [
    { amount: 100, unit: 'g', label: '100g bag' },
    { amount: 200, unit: 'g', label: '200g bag' },
    { amount: 300, unit: 'g', label: '300g bag' },
    { amount: 500, unit: 'g', label: '500g bag' },
  ],

  // ── OILS ──
  'olive oil': [
    { amount: 250,  unit: 'ml', label: '250ml bottle', notes: 'smallest available' },
    { amount: 500,  unit: 'ml', label: '500ml bottle', notes: 'most common' },
    { amount: 750,  unit: 'ml', label: '750ml bottle' },
    { amount: 1000, unit: 'ml', label: '1L bottle' },
  ],
  'coconut oil': [
    { amount: 200, unit: 'ml', label: '200ml jar' },
    { amount: 400, unit: 'ml', label: '400ml jar' },
    { amount: 500, unit: 'ml', label: '500ml jar' },
  ],
  'rapeseed oil': [
    { amount: 500,  unit: 'ml', label: '500ml bottle' },
    { amount: 750,  unit: 'ml', label: '750ml bottle' },
    { amount: 1000, unit: 'ml', label: '1L bottle' },
  ],
  'sunflower oil': [
    { amount: 500,  unit: 'ml', label: '500ml bottle' },
    { amount: 750,  unit: 'ml', label: '750ml bottle' },
    { amount: 1000, unit: 'ml', label: '1L bottle' },
  ],
  'sesame oil': [
    { amount: 100, unit: 'ml', label: '100ml bottle', notes: 'smallest available' },
    { amount: 200, unit: 'ml', label: '200ml bottle', notes: 'most common' },
    { amount: 500, unit: 'ml', label: '500ml bottle' },
  ],

  // ── CONDIMENTS & SPICES ──
  'cinnamon': [
    { amount: 20, unit: 'g', label: '20g jar', notes: 'smallest available' },
    { amount: 30, unit: 'g', label: '30g jar', notes: 'most common' },
    { amount: 50, unit: 'g', label: '50g bag' },
  ],
  'turmeric': [
    { amount: 30,  unit: 'g', label: '30g jar' },
    { amount: 50,  unit: 'g', label: '50g bag' },
    { amount: 100, unit: 'g', label: '100g bag' },
  ],
  'ginger': [
    { amount: 50,  unit: 'g', label: 'ginger root (~50g)' },
    { amount: 100, unit: 'g', label: 'ginger root (~100g)' },
  ],
  'honey': [
    { amount: 250, unit: 'g', label: '250g jar', notes: 'smallest common size' },
    { amount: 500, unit: 'g', label: '500g jar', notes: 'most common' },
    { amount: 750, unit: 'g', label: '750g jar' },
  ],
  'soy sauce': [
    { amount: 150, unit: 'ml', label: '150ml bottle' },
    { amount: 200, unit: 'ml', label: '200ml bottle', notes: 'most common' },
    { amount: 500, unit: 'ml', label: '500ml bottle' },
  ],
  'tomato paste': [
    { amount: 70,  unit: 'g', label: '70g tube' },
    { amount: 100, unit: 'g', label: '100g tin' },
    { amount: 150, unit: 'g', label: '150g tube' },
    { amount: 200, unit: 'g', label: '200g tin' },
  ],
  'granola': [
    { amount: 375, unit: 'g', label: '375g bag' },
    { amount: 500, unit: 'g', label: '500g bag', notes: 'most common' },
    { amount: 750, unit: 'g', label: '750g bag' },
  ],
  'muesli': [
    { amount: 500,  unit: 'g', label: '500g box', notes: 'most common' },
    { amount: 750,  unit: 'g', label: '750g box' },
    { amount: 1000, unit: 'g', label: '1kg box' },
  ],
  'rosemary': [
    { amount: 1,  unit: 'stk', label: 'bunch fresh rosemary', notes: 'sold as 1 bunch' },
    { amount: 15, unit: 'g',   label: '15g dried jar' },
  ],
}

export function parseQuantityToGrams(
  quantityMass: string,
): { grams: number; unit: 'g' | 'ml' | 'stk' } {
  const str = quantityMass.toLowerCase().trim()
  const match = str.match(/^([\d.]+)\s*(kg|g|l|ml|litre|liter|stk|st|piece|pieces|pcs?|x)?/)

  if (!match) return { grams: 100, unit: 'g' }

  let amount = parseFloat(match[1])
  const rawUnit = match[2] ?? 'g'

  if (rawUnit === 'kg') return { grams: amount * 1000, unit: 'g' }
  if (rawUnit === 'g') return { grams: amount, unit: 'g' }
  if (rawUnit === 'l' || rawUnit === 'litre' || rawUnit === 'liter') return { grams: amount * 1000, unit: 'ml' }
  if (rawUnit === 'ml') return { grams: amount, unit: 'ml' }
  return { grams: amount, unit: 'stk' }
}

export function resolvePackSize(
  canonicalName: string,
  weeklyTotal: number,
  baseUnit: 'g' | 'ml' | 'stk' = 'g',
): ResolvedPack {
  const normalised = canonicalName.toLowerCase().trim()

  // Step 1: Convert gram-specified countable items to unit count before pack lookup.
  // Prevents "194g eggs" being treated as 194 stk.
  const gramsPerUnit = GRAM_TO_UNIT[normalised]
  if (gramsPerUnit && baseUnit === 'g') {
    const unitCount = Math.ceil(weeklyTotal / gramsPerUnit)
    return resolvePackSize(canonicalName, unitCount, 'stk')
  }

  // Step 2: Convert to retail unit (e.g. oil grams → ml)
  const conversion = UNIT_CONVERSIONS[normalised]
  const convertedTotal = conversion ? Math.ceil(weeklyTotal * conversion.multiply) : weeklyTotal
  const effectiveUnit = conversion ? conversion.target_unit : baseUnit

  const packs = PACK_SIZES[normalised]

  if (!packs || packs.length === 0) {
    console.warn('packSizeResolver: no pack data for:', canonicalName)
    return {
      ingredient: canonicalName,
      weekly_total_g_or_ml: convertedTotal,
      base_unit: effectiveUnit,
      recommended_pack: {
        amount: convertedTotal,
        unit: effectiveUnit,
        label: `${convertedTotal}${effectiveUnit}`,
        notes: 'no pack size data',
      },
      packs_needed: 1,
      shopping_label: `${convertedTotal}${effectiveUnit}`,
    }
  }

  // Step 3: Pick the pack that covers the need in the fewest units.
  // Prefer single-pack coverage; among multi-pack options pick lowest count.
  let best = packs[0]
  let bestCount = Math.ceil(convertedTotal / packs[0].amount)

  for (const pack of packs) {
    const needed = Math.ceil(convertedTotal / pack.amount)
    if (needed < bestCount || (needed === 1 && bestCount > 1)) {
      best = pack
      bestCount = needed
    }
  }

  const packsNeeded = Math.ceil(convertedTotal / best.amount)

  // Strip any leading "N× " quantity prefix already baked into a pack label.
  // Prevents "1× 1× 400g tin" double-prefix bugs.
  const cleanLabel = best.label.replace(/^\d+×\s*/i, '')

  const shoppingLabel = packsNeeded === 1
    ? `1× ${cleanLabel}`
    : `${packsNeeded}× ${cleanLabel}`

  return {
    ingredient: canonicalName,
    weekly_total_g_or_ml: convertedTotal,
    base_unit: effectiveUnit,
    recommended_pack: best,
    packs_needed: packsNeeded,
    shopping_label: shoppingLabel,
  }
}
